import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  ArrowPathIcon,
  QueueListIcon,
  PencilSquareIcon,
  StopCircleIcon,
  TrashIcon,
} from '@heroicons/react/20/solid'
import { Button } from '../components/button'
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle,
} from '../components/dialog'
import { Field, Label } from '../components/fieldset'
import { Heading } from '../components/heading'
import { Input } from '../components/input'
import { Select } from '../components/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/table'
import { Textarea } from '../components/textarea'
import { AdminEvent, AdminJobRun, FlightPayload } from './types'
import {
  AdminEventFilter,
  buildAdminEventsURL,
  eventSubjectLabel,
} from './admin_events'
import {
  AdminJobAction,
  canCancelAdminJob,
  canExpireAdminJob,
  runAdminJobAction,
} from './admin_job_actions'
import { AdminMaintenance } from './admin_maintenance'
import { useAuthStatus } from './user_dropdown'

const FLIGHT_TYPES = ['site flight', 'hike and fly', 'tandem']

type AdminView = 'flights' | 'maintenance' | 'jobs' | 'events'

type FlightEdit = {
  flight: FlightPayload
  title: string
  type: string
  post: string
}

type PendingJobAction = {
  action: AdminJobAction
  job: AdminJobRun
}

function formatDateTime(value?: string | Date) {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '-'
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date)
}

function responseError(response: Response, fallback: string) {
  return response.json()
    .then(body => body.detail || body.title || fallback)
    .catch(() => fallback)
}

function EmptyRow({ columns, children }: { columns: number; children: React.ReactNode }) {
  return (
    <TableRow>
      <TableCell colSpan={columns} className="py-12 text-center text-zinc-500 dark:text-zinc-400">
        {children}
      </TableCell>
    </TableRow>
  )
}

function AdminFlights() {
  const [flights, setFlights] = useState<FlightPayload[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [editing, setEditing] = useState<FlightEdit | null>(null)
  const [deleting, setDeleting] = useState<FlightPayload | null>(null)
  const [saving, setSaving] = useState(false)

  const loadFlights = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const response = await fetch('/v0/flights?limit=100')
      if (!response.ok) throw new Error(await responseError(response, 'Failed to load flights'))
      const body = await response.json()
      setFlights(body.flights ?? [])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load flights')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadFlights()
  }, [loadFlights])

  const openEditor = async (flight: FlightPayload) => {
    setError('')
    try {
      const response = await fetch(`/v0/flight/${flight.id}`)
      if (!response.ok) throw new Error(await responseError(response, 'Failed to load flight'))
      const detail: FlightPayload = await response.json()
      setEditing({
        flight,
        title: detail.title ?? '',
        type: detail.type,
        post: detail.post ?? '',
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load flight')
    }
  }

  const saveFlight = async () => {
    if (!editing) return
    setSaving(true)
    setError('')
    try {
      const response = await fetch(`/v0/flight/${editing.flight.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: editing.title.trim(),
          type: editing.type,
          post: editing.post,
        }),
      })
      if (!response.ok) throw new Error(await responseError(response, 'Failed to update flight'))
      setFlights(current => current.map(f => (
        f.id === editing.flight.id
          ? { ...f, title: editing.title.trim(), type: editing.type }
          : f
      )))
      setEditing(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update flight')
    } finally {
      setSaving(false)
    }
  }

  const deleteFlight = async () => {
    if (!deleting) return
    setSaving(true)
    setError('')
    try {
      const response = await fetch(`/v0/flight/${deleting.id}`, { method: 'DELETE' })
      if (!response.ok) throw new Error(await responseError(response, 'Failed to delete flight'))
      setFlights(current => current.filter(f => f.id !== deleting.id))
      setDeleting(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete flight')
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      <div className="mb-4 flex items-center justify-between gap-4">
        <p className="text-sm text-zinc-500 dark:text-zinc-400">
          Showing the 100 most recent flights.
        </p>
        <Button plain onClick={loadFlights} aria-label="Refresh flights" title="Refresh flights">
          <ArrowPathIcon />
        </Button>
      </div>
      {error && (
        <p role="alert" className="mb-4 border-l-2 border-red-500 pl-3 text-sm text-red-600 dark:text-red-400">
          {error}
        </p>
      )}
      <Table dense striped>
        <TableHead>
          <TableRow>
            <TableHeader>Flight</TableHeader>
            <TableHeader>Pilot</TableHeader>
            <TableHeader>Date</TableHeader>
            <TableHeader>Type</TableHeader>
            <TableHeader className="text-right">Actions</TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading && <EmptyRow columns={5}>Loading flights...</EmptyRow>}
          {!loading && flights.length === 0 && <EmptyRow columns={5}>No flights found.</EmptyRow>}
          {!loading && flights.map(flight => (
            <TableRow key={flight.id}>
              <TableCell>
                <Link
                  to={`/flight/${flight.id}`}
                  className="font-medium text-zinc-950 hover:text-[#EF8354] dark:text-white"
                >
                  {flight.title || flight.takeoff_name || `Flight #${flight.id}`}
                </Link>
                <div className="text-xs text-zinc-500">#{flight.id}</div>
              </TableCell>
              <TableCell>{flight.pilot_name || flight.pilot_nick || `Pilot #${flight.pilot_id}`}</TableCell>
              <TableCell>{formatDateTime(flight.start_time)}</TableCell>
              <TableCell>{flight.type}</TableCell>
              <TableCell>
                <div className="flex justify-end gap-1">
                  <Button plain onClick={() => openEditor(flight)} aria-label="Edit flight" title="Edit flight">
                    <PencilSquareIcon />
                  </Button>
                  <Button plain onClick={() => setDeleting(flight)} aria-label="Delete flight" title="Delete flight">
                    <TrashIcon className="text-red-500" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Dialog open={editing !== null} onClose={() => !saving && setEditing(null)}>
        <DialogTitle>Edit flight</DialogTitle>
        <DialogDescription>
          Update the public details for flight #{editing?.flight.id}.
        </DialogDescription>
        <DialogBody>
          {error && (
            <p role="alert" className="mb-4 border-l-2 border-red-500 pl-3 text-sm text-red-600 dark:text-red-400">
              {error}
            </p>
          )}
          {editing && (
            <div className="space-y-5">
              <Field>
                <Label>Title</Label>
                <Input
                  value={editing.title}
                  onChange={event => setEditing({ ...editing, title: event.target.value })}
                />
              </Field>
              <Field>
                <Label>Type</Label>
                <Select
                  value={editing.type}
                  onChange={event => setEditing({ ...editing, type: event.target.value })}
                >
                  {!FLIGHT_TYPES.includes(editing.type) && (
                    <option value={editing.type}>{editing.type}</option>
                  )}
                  {FLIGHT_TYPES.map(type => <option key={type} value={type}>{type}</option>)}
                </Select>
              </Field>
              <Field>
                <Label>Post</Label>
                <Textarea
                  rows={8}
                  value={editing.post}
                  onChange={event => setEditing({ ...editing, post: event.target.value })}
                />
              </Field>
            </div>
          )}
        </DialogBody>
        <DialogActions>
          <Button plain onClick={() => setEditing(null)} disabled={saving}>Cancel</Button>
          <Button color="airtime" onClick={saveFlight} disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleting !== null} onClose={() => !saving && setDeleting(null)}>
        <DialogTitle>Delete flight?</DialogTitle>
        <DialogDescription>
          Flight #{deleting?.id} and its related data will be permanently deleted.
        </DialogDescription>
        {error && (
          <DialogBody>
            <p role="alert" className="border-l-2 border-red-500 pl-3 text-sm text-red-600 dark:text-red-400">
              {error}
            </p>
          </DialogBody>
        )}
        <DialogActions>
          <Button plain onClick={() => setDeleting(null)} disabled={saving}>Cancel</Button>
          <Button color="red" onClick={deleteFlight} disabled={saving}>
            {saving ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

function stateClasses(state: string) {
  switch (state) {
    case 'completed':
      return 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300'
    case 'discarded':
    case 'cancelled':
      return 'bg-red-50 text-red-700 dark:bg-red-500/10 dark:text-red-300'
    case 'running':
      return 'bg-blue-50 text-blue-700 dark:bg-blue-500/10 dark:text-blue-300'
    default:
      return 'bg-zinc-100 text-zinc-700 dark:bg-white/10 dark:text-zinc-300'
  }
}

function AdminJobs({ onViewEvents }: { onViewEvents: (job: AdminJobRun) => void }) {
  const [jobs, setJobs] = useState<AdminJobRun[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [pendingAction, setPendingAction] = useState<PendingJobAction | null>(null)
  const [acting, setActing] = useState(false)

  const loadJobs = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const response = await fetch('/v0/admin/jobs?limit=100')
      if (!response.ok) throw new Error(await responseError(response, 'Failed to load job runs'))
      const body = await response.json()
      setJobs(body.jobs ?? [])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load job runs')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadJobs()
  }, [loadJobs])

  const applyJobAction = async () => {
    if (!pendingAction) return
    setActing(true)
    setError('')
    setNotice('')
    try {
      await runAdminJobAction(fetch, pendingAction.job.id, pendingAction.action)
      setNotice(
        pendingAction.action === 'cancel'
          ? `Cancellation requested for job #${pendingAction.job.id}.`
          : `Job #${pendingAction.job.id} expired.`
      )
      setPendingAction(null)
      await loadJobs()
    } catch (err) {
      setPendingAction(null)
      setError(err instanceof Error ? err.message : `Failed to ${pendingAction.action} job`)
    } finally {
      setActing(false)
    }
  }

  return (
    <>
      <div className="mb-4 flex items-center justify-between gap-4">
        <p className="text-sm text-zinc-500 dark:text-zinc-400">
          Recent scheduled and background job runs.
        </p>
        <Button plain onClick={loadJobs} aria-label="Refresh job runs" title="Refresh job runs">
          <ArrowPathIcon />
        </Button>
      </div>
      {error && (
        <p role="alert" className="mb-4 border-l-2 border-red-500 pl-3 text-sm text-red-600 dark:text-red-400">
          {error}
        </p>
      )}
      {notice && (
        <p role="status" className="mb-4 border-l-2 border-emerald-500 pl-3 text-sm text-emerald-700 dark:text-emerald-300">
          {notice}
        </p>
      )}
      <Table dense striped>
        <TableHead>
          <TableRow>
            <TableHeader>Job</TableHeader>
            <TableHeader>State</TableHeader>
            <TableHeader>Scheduled</TableHeader>
            <TableHeader>Finished</TableHeader>
            <TableHeader>Attempts</TableHeader>
            <TableHeader>Latest failure</TableHeader>
            <TableHeader className="text-right">Actions</TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading && <EmptyRow columns={7}>Loading job runs...</EmptyRow>}
          {!loading && jobs.length === 0 && <EmptyRow columns={7}>No job runs found.</EmptyRow>}
          {!loading && jobs.map(job => (
            <TableRow key={job.id}>
              <TableCell>
                <div className="font-medium text-zinc-950 dark:text-white">{job.kind}</div>
                <div className="text-xs text-zinc-500" title={job.args}>
                  #{job.id}
                </div>
              </TableCell>
              <TableCell>
                <span className={`inline-flex rounded px-2 py-0.5 text-xs font-medium ${stateClasses(job.state)}`}>
                  {job.state}
                </span>
                {job.cancel_requested_at && (
                  <div className="mt-1 text-xs text-amber-700 dark:text-amber-300">
                    Cancellation requested
                  </div>
                )}
              </TableCell>
              <TableCell>{formatDateTime(job.scheduled_at)}</TableCell>
              <TableCell>{formatDateTime(job.finalized_at)}</TableCell>
              <TableCell>{job.attempt} / {job.max_attempts}</TableCell>
              <TableCell className="max-w-sm whitespace-normal">
                {job.latest_error ? (
                  <>
                    <div className="text-sm text-red-700 dark:text-red-300">{job.latest_error.message}</div>
                    <div className="text-xs text-zinc-500">
                      Attempt {job.latest_error.attempt}, {formatDateTime(job.latest_error.at)}
                    </div>
                  </>
                ) : '-'}
              </TableCell>
              <TableCell>
                <div className="flex min-w-20 justify-end gap-1">
                  {canCancelAdminJob(job.state, job.cancel_requested_at) && (
                    <Button
                      plain
                      onClick={() => setPendingAction({ action: 'cancel', job })}
                      aria-label={`Cancel job #${job.id}`}
                      title="Cancel job"
                    >
                      <StopCircleIcon />
                    </Button>
                  )}
                  <Button
                    plain
                    onClick={() => onViewEvents(job)}
                    aria-label={`View events for job #${job.id}`}
                    title="View events"
                  >
                    <QueueListIcon />
                  </Button>
                  {canExpireAdminJob(job.state) && (
                    <Button
                      plain
                      onClick={() => setPendingAction({ action: 'expire', job })}
                      aria-label={`Expire job #${job.id}`}
                      title="Expire completed job"
                    >
                      <TrashIcon />
                    </Button>
                  )}
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Dialog open={pendingAction !== null} onClose={() => !acting && setPendingAction(null)}>
        <DialogTitle>
          {pendingAction?.action === 'cancel' ? 'Cancel job?' : 'Expire completed job?'}
        </DialogTitle>
        <DialogDescription>
          {pendingAction?.action === 'cancel'
            ? 'The job will not be retried. A running worker will stop when it observes the cancellation request.'
            : 'This permanently removes the completed job and its failure history from the job runs list.'}
        </DialogDescription>
        {pendingAction && (
          <DialogBody>
            <p className="text-sm text-zinc-700 dark:text-zinc-300">
              {pendingAction.job.kind} #{pendingAction.job.id}
            </p>
          </DialogBody>
        )}
        <DialogActions>
          <Button plain disabled={acting} onClick={() => setPendingAction(null)}>Keep job</Button>
          <Button color="red" disabled={acting} onClick={applyJobAction}>
            {acting
              ? 'Working...'
              : pendingAction?.action === 'cancel' ? 'Cancel job' : 'Expire job'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

function AdminEvents({ initialFilter }: { initialFilter: AdminEventFilter }) {
  const [filter, setFilter] = useState<AdminEventFilter>(initialFilter)
  const [appliedFilter, setAppliedFilter] = useState<AdminEventFilter>(initialFilter)
  const [events, setEvents] = useState<AdminEvent[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const loadEvents = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const response = await fetch(buildAdminEventsURL(appliedFilter))
      if (!response.ok) throw new Error(await responseError(response, 'Failed to load events'))
      const body = await response.json()
      setEvents(body.events ?? [])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load events')
    } finally {
      setLoading(false)
    }
  }, [appliedFilter])

  useEffect(() => {
    loadEvents()
  }, [loadEvents])

  const applyFilters = (event: React.FormEvent) => {
    event.preventDefault()
    setAppliedFilter({ ...filter })
  }

  const clearFilters = () => {
    setFilter({})
    setAppliedFilter({})
  }

  return (
    <>
      <form onSubmit={applyFilters} className="mb-5 grid gap-3 rounded-lg bg-zinc-50 p-4 dark:bg-white/5 sm:grid-cols-2 lg:grid-cols-6">
        <Field>
          <Label>Subject type</Label>
          <Input
            value={filter.subjectType ?? ''}
            placeholder="pilot, job, flight..."
            onChange={event => setFilter({ ...filter, subjectType: event.target.value })}
          />
        </Field>
        <Field>
          <Label>Subject ID</Label>
          <Input
            value={filter.subjectId ?? ''}
            placeholder="320"
            onChange={event => setFilter({ ...filter, subjectId: event.target.value })}
          />
        </Field>
        <Field>
          <Label>Kind</Label>
          <Input
            value={filter.kind ?? ''}
            placeholder="provider.crawl.failed"
            onChange={event => setFilter({ ...filter, kind: event.target.value })}
          />
        </Field>
        <Field>
          <Label>Retention</Label>
          <Select
            value={filter.retention ?? ''}
            onChange={event => setFilter({
              ...filter,
              retention: event.target.value as AdminEventFilter['retention'],
            })}
          >
            <option value="">Any</option>
            <option value="ephemeral">Ephemeral</option>
            <option value="persistent">Persistent</option>
          </Select>
        </Field>
        <Field className="sm:col-span-2">
          <Label>Text or structured data</Label>
          <Input
            value={filter.query ?? ''}
            placeholder="rate limited"
            onChange={event => setFilter({ ...filter, query: event.target.value })}
          />
        </Field>
        <div className="flex items-end gap-2 sm:col-span-2 lg:col-span-6 lg:justify-end">
          <Button type="button" plain onClick={clearFilters}>Clear</Button>
          <Button type="submit" color="airtime">Search events</Button>
          <Button type="button" plain onClick={loadEvents} aria-label="Refresh events" title="Refresh events">
            <ArrowPathIcon />
          </Button>
        </div>
      </form>

      {error && (
        <p role="alert" className="mb-4 border-l-2 border-red-500 pl-3 text-sm text-red-600 dark:text-red-400">
          {error}
        </p>
      )}
      <Table dense striped>
        <TableHead>
          <TableRow>
            <TableHeader>Time</TableHeader>
            <TableHeader>Event</TableHeader>
            <TableHeader>Retention</TableHeader>
            <TableHeader>Subjects</TableHeader>
            <TableHeader>Data</TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading && <EmptyRow columns={5}>Loading events...</EmptyRow>}
          {!loading && events.length === 0 && <EmptyRow columns={5}>No events found.</EmptyRow>}
          {!loading && events.map(event => (
            <TableRow key={event.id}>
              <TableCell className="whitespace-nowrap">{formatDateTime(event.created_at)}</TableCell>
              <TableCell className="max-w-md whitespace-normal">
                <div className="font-medium text-zinc-950 dark:text-white">{event.kind}</div>
                <div className="text-sm text-zinc-600 dark:text-zinc-300">{event.message || '-'}</div>
                <div className="text-xs text-zinc-500">#{event.id}</div>
              </TableCell>
              <TableCell>{event.retention}</TableCell>
              <TableCell className="max-w-xs whitespace-normal">
                <div className="flex flex-wrap gap-1">
                  {event.subjects.map(subject => (
                    <span
                      key={`${subject.type}:${subject.id}`}
                      className="rounded bg-zinc-100 px-1.5 py-0.5 text-xs text-zinc-700 dark:bg-white/10 dark:text-zinc-300"
                    >
                      {eventSubjectLabel(subject)}
                    </span>
                  ))}
                </div>
              </TableCell>
              <TableCell className="max-w-sm whitespace-normal">
                <pre className="overflow-auto text-xs text-zinc-600 dark:text-zinc-300">
                  {JSON.stringify(event.data, null, 2)}
                </pre>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </>
  )
}

export function AdminPage() {
  const { userData, loading } = useAuthStatus()
  const [view, setView] = useState<AdminView>('flights')
  const [eventFilter, setEventFilter] = useState<AdminEventFilter>({})

  if (loading) {
    return <div className="py-12 text-center text-sm text-zinc-500">Loading...</div>
  }

  if (!userData.authenticated || !userData.pilot?.is_admin) {
    return (
      <div className="mx-auto max-w-3xl py-12">
        <Heading>Access denied</Heading>
        <p className="mt-2 text-sm text-zinc-500 dark:text-zinc-400">
          Administrator access is required.
        </p>
      </div>
    )
  }

  const content = {
    flights: <AdminFlights />,
    maintenance: <AdminMaintenance />,
    jobs: <AdminJobs onViewEvents={job => {
      setEventFilter({ subjectType: 'job', subjectId: String(job.id) })
      setView('events')
    }} />,
    events: <AdminEvents key={JSON.stringify(eventFilter)} initialFilter={eventFilter} />,
  }[view]

  return (
    <main className="py-6">
      <div className="mb-6">
        <Heading>Administration</Heading>
      </div>
      <div className="mb-6 flex border-b border-zinc-950/10 dark:border-white/10" role="tablist">
        {([
          ['flights', 'Flights'],
          ['maintenance', 'Maintenance'],
          ['jobs', 'Job runs'],
          ['events', 'Events'],
        ] as [AdminView, string][]).map(([id, label]) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={view === id}
            onClick={() => setView(id)}
            className={`border-b-2 px-4 py-2 text-sm font-medium ${
              view === id
                ? 'border-[#EF8354] text-zinc-950 dark:text-white'
                : 'border-transparent text-zinc-500 hover:text-zinc-950 dark:hover:text-white'
            }`}
          >
            {label}
          </button>
        ))}
      </div>
      {content}
    </main>
  )
}
